import { useQuery } from 'react-query';
import {
  getCompanies,
  getCompany,
  getCompanyProjectSubmissions,
  getExerciseSubmissions,
  getCompanyOfficeHours,
  getAssessmentTests,
  getAssessmentTestSession,
} from '../../api/admin';
import useAuth from '../use-auth';
import { queryOptions } from '../use-hydra';

export function useGetCompanies() {
  const { getTokenSilently, isAuthenticated } = useAuth();

  const query = useQuery(
    ['get-companies', isAuthenticated],
    async () => {
      if (!isAuthenticated) {
        return null;
      }
      const token = await getTokenSilently();
      return getCompanies(token);
    },
    queryOptions
  );

  return query;
}

export function useGetCompany(id: number) {
  const { getTokenSilently, isAuthenticated } = useAuth();

  const query = useQuery(
    ['get-company', id, isAuthenticated],
    async () => {
      if (!isAuthenticated) {
        return null;
      }
      const token = await getTokenSilently();
      return getCompany(token, id);
    },
    queryOptions
  );

  return query;
}

export function useGetExerciseSubmissions(companyId: number) {
  const { getTokenSilently, isAuthenticated } = useAuth();

  const query = useQuery(
    ['get-exercise-submissions', companyId, isAuthenticated],
    async () => {
      if (!isAuthenticated) {
        return null;
      }
      const token = await getTokenSilently();
      return getExerciseSubmissions(token, { companyId });
    },
    queryOptions
  );

  return query;
}

export function useGetCompanyProjectSubmissions(companyId: number) {
  const { getTokenSilently, isAuthenticated } = useAuth();

  const query = useQuery(
    ['get-company-project-submissions', companyId, isAuthenticated],
    async () => {
      if (!isAuthenticated) {
        return null;
      }
      const token = await getTokenSilently();
      return getCompanyProjectSubmissions(token, { companyId });
    },
    queryOptions
  );

  return query;
}

export function useGetCompanyOfficeHours(companyId: number) {
  const { getTokenSilently, isAuthenticated } = useAuth();

  const query = useQuery(
    ['get-company-office-hours', companyId, isAuthenticated],
    async () => {
      if (!isAuthenticated) {
        return null;
      }
      const token = await getTokenSilently();
      return getCompanyOfficeHours(token, { companyId });
    },
    queryOptions
  );

  return query;
}

export function useGetAssessmentTests(companyId: number) {
  const { getTokenSilently, isAuthenticated } = useAuth();

  const query = useQuery(
    ['get-assessment-tests', companyId, isAuthenticated],
    async () => {
      if (!isAuthenticated) {
        return null;
      }
      const token = await getTokenSilently();
      return getAssessmentTests(token, { companyId });
    },
    queryOptions
  );

  return query;
}

export function useGetAssessmentTestSession(
  companyId: number,
  assessmentTestSessionId: number
) {
  const { getTokenSilently, isAuthenticated } = useAuth();

  const query = useQuery(
    [
      'get-assessment-test-session',
      companyId,
      assessmentTestSessionId,
      isAuthenticated,
    ],
    async () => {
      if (!isAuthenticated) {
        return null;
      }
      const token = await getTokenSilently();
      return getAssessmentTestSession(token, {
        companyId,
        assessmentTestSessionId,
      });
    },
    queryOptions
  );

  return query;
}
